import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiError, formatUAH } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit2, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  category: "", name: "", abbreviation: "", manufacturer: "",
  sale_price: 0, iol_cost: 0, medical_service: 0, anesthesiologist: 0,
  cost_purchase: 0, note: "", is_active: true,
};

export default function PriceList() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try { const r = await api.get("/price-list"); setItems(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => [...new Set(items.map((i) => i.category))].sort(), [items]);

  const filtered = items.filter((i) => {
    if (catFilter !== "all" && i.category !== catFilter) return false;
    if (search && !`${i.name} ${i.abbreviation} ${i.manufacturer}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const save = async () => {
    try {
      if (editing) await api.put(`/price-list/${editing.id}`, form);
      else await api.post("/price-list", form);
      toast.success("Збережено"); setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Видалити "${it.name}"?`)) return;
    try { await api.delete(`/price-list/${it.id}`); toast.success("Видалено"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (it) => { setEditing(it); setForm({ ...EMPTY, ...it }); setOpen(true); };

  return (
    <div data-testid="pricelist-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-3xl font-bold text-[#0F2027]">Прейскурант</h1>
          <p className="text-sm text-slate-500">{items.length} позицій · послуги, ІОЛ та маніпуляції</p>
        </div>
        <Button onClick={startCreate} data-testid="add-price-item" className="bg-[#0F2027] hover:bg-[#0F2027]/90">
          <Plus className="h-4 w-4 mr-2" /> Додати позицію
        </Button>
      </div>

      <Card className="gv-card">
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Пошук</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input data-testid="pricelist-search" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" placeholder="Назва, абревіатура, виробник" />
            </div>
          </div>
          <div className="w-64">
            <Label className="text-xs">Категорія</Label>
            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger data-testid="pricelist-cat"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Усі категорії</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="gv-card"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0">
            <tr>
              <th className="text-left p-3">Категорія</th>
              <th className="text-left p-3">Назва</th>
              <th className="text-left p-3">Абрев.</th>
              <th className="text-right p-3">Загальна</th>
              <th className="text-right p-3">ІОЛ</th>
              <th className="text-right p-3">Мед.</th>
              <th className="text-right p-3">Анест.</th>
              <th className="text-right p-3">Собівартість</th>
              <th className="text-right p-3">Прибуток</th>
              <th className="text-left p-3">Примітка</th>
              <th className="text-center p-3">Активна</th>
              <th className="text-right p-3 w-24">Дії</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it) => (
              <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="p-3 text-xs font-semibold text-[#0D9488]">{it.category}</td>
                <td className="p-3 font-medium text-[#0F2027]">{it.name}</td>
                <td className="p-3 text-xs font-mono text-slate-600">{it.abbreviation}</td>
                <td className="p-3 text-right tabular-nums font-semibold">{formatUAH(it.sale_price)}</td>
                <td className="p-3 text-right tabular-nums text-slate-600">{it.iol_cost > 0 ? formatUAH(it.iol_cost) : "—"}</td>
                <td className="p-3 text-right tabular-nums text-slate-600">{it.medical_service > 0 ? formatUAH(it.medical_service) : "—"}</td>
                <td className="p-3 text-right tabular-nums text-slate-600">{it.anesthesiologist > 0 ? formatUAH(it.anesthesiologist) : "—"}</td>
                <td className="p-3 text-right tabular-nums text-red-700">{it.cost_purchase > 0 ? formatUAH(it.cost_purchase) : "—"}</td>
                <td className="p-3 text-right tabular-nums font-semibold text-emerald-700">{formatUAH(it.profit)}</td>
                <td className="p-3 text-xs text-slate-500 max-w-xs truncate">{it.note}</td>
                <td className="p-3 text-center">
                  <Badge className={it.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}>
                    {it.is_active ? "Так" : "Ні"}
                  </Badge>
                </td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" onClick={() => startEdit(it)} data-testid={`edit-pi-${it.id}`}><Edit2 className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(it)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={12} className="p-8 text-center text-slate-400">Немає позицій</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Редагувати позицію" : "Створити позицію"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label>Назва</Label><Input data-testid="pi-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Категорія</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            <div><Label>Виробник</Label><Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} /></div>
            <div><Label>Абревіатура</Label><Input value={form.abbreviation} onChange={(e) => setForm({ ...form, abbreviation: e.target.value })} /></div>
            <div></div>
            <div><Label>Загальна вартість (₴)</Label><Input type="number" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: parseFloat(e.target.value || 0) })} /></div>
            <div><Label>Вартість ІОЛ (₴)</Label><Input type="number" value={form.iol_cost} onChange={(e) => setForm({ ...form, iol_cost: parseFloat(e.target.value || 0) })} /></div>
            <div><Label>Медичне обслуговування (₴)</Label><Input type="number" value={form.medical_service} onChange={(e) => setForm({ ...form, medical_service: parseFloat(e.target.value || 0) })} /></div>
            <div><Label>Анестезіолог (₴)</Label><Input type="number" value={form.anesthesiologist} onChange={(e) => setForm({ ...form, anesthesiologist: parseFloat(e.target.value || 0) })} /></div>
            <div><Label>Собівартість / закупка (₴)</Label><Input type="number" value={form.cost_purchase} onChange={(e) => setForm({ ...form, cost_purchase: parseFloat(e.target.value || 0) })} /></div>
            <div className="md:col-span-2"><Label>Примітка</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
            <div className="md:col-span-2 flex items-center gap-2">
              <input type="checkbox" id="pi-active" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              <Label htmlFor="pi-active">Активна</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Скасувати</Button>
            <Button onClick={save} data-testid="save-pi" className="bg-[#0F2027]">Зберегти</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
